import { useCallback, useEffect, useState } from "react";

import { useAudioRecorder } from "@/hooks/use-audio-recorder";

interface UseVoiceToTextOptions {
  onTranscription: (text: string) => void;
  transcribeMedia: (file: Blob) => Promise<{ text?: string } | null>;
}

export const useVoiceToText = ({
  onTranscription,
  transcribeMedia,
}: UseVoiceToTextOptions) => {
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState<string | null>(
    null
  );
  const {
    startRecording,
    stopRecording,
    reset,
    isRecording,
    audioBlob,
    levels,
    error: recordingError,
    isSupported: isRecorderSupported,
  } = useAudioRecorder({ bars: 60 });

  const startVoiceRecording = useCallback(() => {
    setTranscriptionError(null);
    return startRecording();
  }, [startRecording]);

  const stopVoiceRecording = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  const resetVoiceState = useCallback(() => {
    reset();
    setIsTranscribing(false);
    setTranscriptionError(null);
  }, [reset]);

  useEffect(() => {
    if (!audioBlob) return;

    let isCancelled = false;

    const transcribe = async () => {
      try {
        setIsTranscribing(true);
        setTranscriptionError(null);
        const response = await transcribeMedia(audioBlob);
        if (isCancelled) {
          return;
        }

        const nextText = response?.text?.trim();
        if (nextText) {
          onTranscription(nextText);
        }
      } catch (error) {
        console.error("Failed to transcribe audio", error);
        if (!isCancelled) {
          setTranscriptionError(
            "We couldn't transcribe that recording. Please try again."
          );
        }
      } finally {
        if (!isCancelled) {
          setIsTranscribing(false);
          reset();
        }
      }
    };

    void transcribe();

    return () => {
      isCancelled = true;
    };
  }, [audioBlob, onTranscription, reset, transcribeMedia]);

  return {
    isRecording,
    isTranscribing,
    transcriptionError,
    recordingError,
    isRecorderSupported,
    levels,
    startRecording: startVoiceRecording,
    stopRecording: stopVoiceRecording,
    resetVoiceState,
  } as const;
};
